import React, { useState, useEffect } from "react";
import { RichText } from "@sitecore-jss/sitecore-jss-react";
import breakpoints from "utils/breakpoints";
import styled from "styled-components";
import PropTypes from "prop-types";
import cx from "classnames";
import ShareDropIcon from "components/components/molecules/ShareDropIcon";
import { isValidTitle } from "utils/validateTitle";
import { Button } from "components/components/atoms";

const Title = styled.h2`
  letter-spacing: -0.5px;
  line-height: 2.625rem;
`;

const Copy = styled.div`
  letter-spacing: 0.11px;
  line-height: 1.625rem;

  @media ${breakpoints.md} {
    max-width: 55rem;
  }
  @media ${breakpoints.lg} {
    max-width: 100%;
  }

  & ul {
    width: fit-content;
    list-style: disc;
    padding-left: 1.25rem;
    margin-left: 1.25rem;
    text-align: justify;
  }

  & ol {
    width: fit-content;
    list-style: upper-greek;
    padding-left: 1.25rem;
    margin-left: 1.25rem;
    text-align: justify;
  }
  & a {
    text-decoration: none;
    color: inherit;
  }

  & a:hover {
    text-decoration: underline;
  }
`;

const SmallTitleAndText = ({
  title,
  text,
  textAlignment = "left",
  textColor = "blue",
  showShare = false,
  bgColor,
  ctaLink,
  ctaText,
  target,
}) => {
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== "undefined" && window.innerWidth
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => {
        setScreenWidth(window.innerWidth);
      };
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  const shareButtonColor = bgColor === "blue" ? "white" : "blue";
  const heading18Regex = /<p tabindex="0" style="text-align: left;">\s*<span style="font-family: tahoma; font-size: 18px; color: rgb\(0, 0, 0\);"><strong>([^<]+)<\/strong><\/span>\s*<\/p>/g;

  const updatedHtmlH3Template =
    '<h3 tabindex="0" style="font-family: tahoma; font-size: 1.25rem; color: rgb(0, 0, 0);"><strong>$1</strong></h3>';

  const removePTabIndexRegex = /<p(?:\s+[^>]*)?\btabindex="0"(?:\s+[^>]*)?>/gi;
  const updatedText = text
    .replace(/<a[^>]*\btarget\s*=\s*["']_blank["'][^>]*>.*?<\/a>/g, (match) => {
      const content = match.match(/<a[^>]*>(.*?)<\/a>/i);
      if (content) {
        const cleanedContent = content[1].replace(
          /<strong>(.*?)<\/strong>/g,
          "$1"
        );
        return match.replace(
          /<a(.*?)>/,
          `<a$1  aria-label="opens in a new window ${cleanedContent}">`
        );
      }
      return match;
    })
    .replace(heading18Regex, updatedHtmlH3Template)

    .replace(
      removePTabIndexRegex,
      '<p style="color:rgb(0,0,0);font-family:tahoma">'
    )
    .replace(/(<(ul|li|h2|h3)[^>]+)tabindex="[^"]*"/gi, "$1")
    .replace(/<h3([^>]*)tabindex="[^"]*"([^>]*)>/gi, "<h3$1$2>")
    .replace("font-size: 18px;", "font-size:1.25rem;")
    .replace(
      //fix table -> adaptive with screen 400 new
      /<table\b/gi,
      screenWidth < 600
        ? '<table style="min-width:90%;max-width:90%;margin-left:-1rem"'
        : '<table style="min-width:90%;max-width:90%'
    );

  return (
    <div
      className={cx("w-full h-full flex flex-col", {
        "text-left": textAlignment === "left",
        "items-center text-center": textAlignment === "center",
        "text-mattamy-blue": textColor === "blue",
        "text-white": textColor === "white",
      })}
    >
      {isValidTitle(title) && (
        <Title
          // in new pr this title inde x remowed consider please
          className={cx("text-4xl font-trade-gothic-20 ", {
            "pr-12 md:pr-0": showShare,
          })}
        >
          {title}
        </Title>
      )}
      <Copy className="mt-3 text-base font-graphie">
        <RichText
          tag="span"
          className="richtext"
          field={{ value: updatedText }}
        />
      </Copy>

      {ctaLink && ctaText && (
        <div className="mt-4">
          <Button
            variant={bgColor === "white" ? "filled" : "outlined"}
            color={
              bgColor === "white" ||
              bgColor === "gray" ||
              (bgColor === "blue" && textColor !== "white")
                ? "mattamy-blue"
                : "white"
            }
            textColor={bgColor === "white" ? "white" : "mattamy-blue"}
            href={ctaLink}
            target={target}
            linkWrapperDescription={ctaText}
          >
            {ctaText}
          </Button>
        </div>
      )}

      {showShare && <ShareDropIcon color={shareButtonColor} />}
    </div>
  );
};

SmallTitleAndText.propTypes = {
  title: PropTypes.string,
  text: PropTypes.string,
  textAlignment: PropTypes.oneOf(["left", "center"]),
  textColor: PropTypes.oneOf(["blue", "white"]),
  showShare: PropTypes.bool,
};

export default SmallTitleAndText;
